import { apiService } from './api';

// Helper function to detect Android devices
const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

// Helper function to check if Web Share API is supported
const canShare = (): boolean => {
  return navigator.share !== undefined;
};

// Helper function to check if device will use share instead of print
export const willUseShare = (): boolean => {
  return isAndroid() && canShare();
};

export const printService = {
  /**
   * Generate and print KOT PDF
   */
  async printKOT(orderId: string, organizationCode: string, autoPrint: boolean = false): Promise<Blob> {
    const response = await apiService.get<Blob>(
      `/orders/${orderId}/kot`,
      { org: organizationCode },
      { responseType: 'blob' as const }
    );

    const blob = response.data; // already a Blob

    if (autoPrint) {
      // Check if Android device and Web Share API is supported
      if (isAndroid() && canShare()) {
        try {
          const file = new File([blob], `KOT_${orderId}.pdf`, { type: 'application/pdf' });
          await navigator.share({
            files: [file],
            title: `KOT - Order ${orderId}`,
            text: 'Kitchen Order Ticket'
          });
        } catch (error) {
          // If share fails or is cancelled, fall back to opening the PDF
          console.log('Share cancelled or failed:', error);
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 2000);
        }
      } else {
        // Normal print flow for non-Android devices
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            // small cleanup delay to allow print dialog to open
            setTimeout(() => window.URL.revokeObjectURL(url), 2000);
          };
        } else {
          // fallback: open the file if popup blocked
          window.location.href = url;
        }
      }
    }

    return blob;
  },

  /**
   * Generate and print Bill PDF
   */
  async printBill(orderId: string, organizationCode: string, autoPrint: boolean = false): Promise<Blob> {
    const response = await apiService.get<Blob>(
      `/orders/${orderId}/bill`,
      { org: organizationCode },
      { responseType: 'blob' as const }
    );

    const blob = response.data;

    if (autoPrint) {
      // Check if Android device and Web Share API is supported
      if (isAndroid() && canShare()) {
        try {
          const file = new File([blob], `Bill_${orderId}.pdf`, { type: 'application/pdf' });
          await navigator.share({
            files: [file],
            title: `Bill - Order ${orderId}`,
            text: 'Customer Bill'
          });
        } catch (error) {
          // If share fails or is cancelled, fall back to opening the PDF
          console.log('Share cancelled or failed:', error);
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 2000);
        }
      } else {
        // Normal print flow for non-Android devices
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            setTimeout(() => window.URL.revokeObjectURL(url), 2000);
          };
        } else {
          window.location.href = url;
        }
      }
    }

    return blob;
  },

  /**
   * Download KOT PDF
   */
  async downloadKOT(orderId: string, organizationCode: string): Promise<void> {
    const response = await apiService.get<Blob>(
      `/orders/${orderId}/kot`,
      { org: organizationCode },
      { responseType: 'blob' as const }
    );

    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `KOT_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download Bill PDF
   */
  async downloadBill(orderId: string, organizationCode: string): Promise<void> {
    const response = await apiService.get<Blob>(
      `/orders/${orderId}/bill`,
      { org: organizationCode },
      { responseType: 'blob' as const }
    );

    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bill_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};
